import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, Button, Modal, Form, Input, message, Popconfirm,
  Tag, Space, Card, Upload, Select, Alert, Progress,
  Tooltip, Divider, Badge, Checkbox, Typography, AutoComplete,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined,
  PlayCircleOutlined, PauseCircleOutlined, DownloadOutlined,
  ReloadOutlined, RedoOutlined, CheckOutlined, MergeOutlined,
  AudioOutlined, ScissorOutlined, DollarOutlined, SoundOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import apiClient from '../utils/apiClient';
import * as XLSX from 'xlsx';
import { safeMap, ensureArray } from '../utils/arrayUtils';
import { useSelector } from 'react-redux';
import ScriptSplitter from './ScriptSplitter';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

let isFetchingRequests = false;

function TextToVoice() {
  const navigate = useNavigate();
  const { user } = useSelector(state => state.auth);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [voices, setVoices] = useState([]);
  const [form] = Form.useForm();
  const abortControllerRef = useRef(null);
  const [playingAudio, setPlayingAudio] = useState(null);
  const audioCache = useRef({});
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [excelVoiceModalVisible, setExcelVoiceModalVisible] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState('');
  const [excelFile, setExcelFile] = useState(null);
  const [txtFile, setTxtFile] = useState(null);
  const [importType, setImportType] = useState('excel');
  const [playingDemo, setPlayingDemo] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [mergeModalVisible, setMergeModalVisible] = useState(false);
  const [mergingAudio, setMergingAudio] = useState(false);
  const [scriptSplitterVisible, setScriptSplitterVisible] = useState(false);
  const [selectedProject, setSelectedProject] = useState('');
  const [availableProjects, setAvailableProjects] = useState([]);

  useEffect(() => {
    fetchVoices();
    fetchRequests(true);
  }, []);

  useEffect(() => {
    if (availableProjects.length > 0) fetchRequests(true);
  }, [selectedProject]);

  useEffect(() => {
    return () => {
      const ctrl = abortControllerRef.current;
      if (ctrl) ctrl.abort();
      Object.values(audioCache.current).forEach(a => { if (a) { a.pause(); a.src = ''; } });
      audioCache.current = {};
    };
  }, []);

  const fetchRequests = async (force = false, page = pagination.current, pageSize = pagination.pageSize) => {
    try {
      if (!force && isFetchingRequests) return;
      isFetchingRequests = true;
      setLoading(true);
      const response = await apiClient.get('/api/text-to-voice/', {
        params: { skip: (page - 1) * pageSize, limit: pageSize },
      });
      let data, total;
      if (Array.isArray(response.data)) {
        data = response.data; total = response.data.length;
      } else if (response.data?.data) {
        data = ensureArray(response.data.data); total = response.data.total || data.length;
      } else {
        data = ensureArray(response.data); total = data.length;
      }
      data = data.sort((a, b) => (a.stt ?? 0) - (b.stt ?? 0));
      const projects = [...new Set(data.map(r => r.project).filter(Boolean))].sort();
      setAvailableProjects(projects);
      setRequests(selectedProject ? data.filter(r => r.project === selectedProject) : data);
      setPagination(prev => ({ ...prev, total }));
    } catch (error) {
      if (error.response?.status !== 403) message.error('Failed to fetch requests');
    } finally {
      setLoading(false);
      isFetchingRequests = false;
    }
  };

  const fetchVoices = async () => {
    try {
      const response = await apiClient.get('/api/voices/');
      const voicesData = response.data?.result?.voices || [];
      setVoices(ensureArray(voicesData.map(v => ({
        code: v.code, name: v.name, gender: v.gender,
        language: v.language || 'en-US', description: v.description, demo: v.demo,
      }))));
    } catch {
      setVoices(ensureArray([
        { code: 'af_bella', name: 'Bella (en-US)', gender: 'female', language: 'en-US' },
        { code: 'af_sarah', name: 'Sarah (en-US)', gender: 'female', language: 'en-US' },
        { code: 'am_adam', name: 'Adam (en-US)', gender: 'male', language: 'en-US' },
        { code: 'am_michael', name: 'Michael (en-US)', gender: 'male', language: 'en-US' },
        { code: 'bf_emma', name: 'Emma (en-UK)', gender: 'female', language: 'en-UK' },
        { code: 'bm_george', name: 'George (en-UK)', gender: 'male', language: 'en-UK' },
      ]));
    }
  };

  const handleAdd = () => {
    setEditingRequest(null);
    form.resetFields();
    // Pre-fill project if currently filtering by project
    if (selectedProject) form.setFieldValue('project', selectedProject);
    setModalVisible(true);
  };
  const handleEdit = (req) => {
    setEditingRequest(req);
    form.setFieldsValue({ stt: req.stt, text: req.text, voice_code: req.voice_code, project: req.project });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      await apiClient.delete(`/api/text-to-voice/${id}`);
      message.success('Deleted');
      fetchRequests(true);
    } catch { message.error('Delete failed'); }
  };

  const handleRetry = async (id) => {
    try {
      await apiClient.post(`/api/text-to-voice/${id}/synthesize`);
      message.success('Retrying...');
      fetchRequests(true);
    } catch { message.error('Retry failed'); }
  };

  const handleRetryDownload = async (id) => {
    try {
      await apiClient.post(`/api/text-to-voice/${id}/retry-download`);
      message.success('Re-downloading file...');
      fetchRequests(true);
    } catch { message.error('Retry download failed'); }
  };

  const handleSynthesize = async (id) => {
    try {
      await apiClient.post(`/api/text-to-voice/${id}/synthesize`);
      message.success('Synthesizing...');
      fetchRequests(true);
    } catch { message.error('Synthesis failed'); }
  };

  const handleSubmit = async (values) => {
    try {
      if (values.text?.length > 1000) { message.error('Maximum 1000 characters'); return; }
      const submitData = { ...values, stt: Number(values.stt) };
      if (editingRequest) {
        await apiClient.put(`/api/text-to-voice/${editingRequest.id}`, submitData);
        message.success('Updated');
      } else {
        await apiClient.post('/api/text-to-voice/', submitData);
        message.success('Created');
      }
      setModalVisible(false);
      fetchRequests(true);
    } catch (error) {
      message.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handlePlayAudio = async (record) => {
    try {
      if (playingAudio === record.id) {
        audioCache.current[record.id]?.pause();
        setPlayingAudio(null);
        return;
      }
      if (playingAudio) audioCache.current[playingAudio]?.pause();

      if (!audioCache.current[record.id]) {
        const response = await apiClient.get(`/api/text-to-voice/audio/${record.id}`, { responseType: 'blob' });
        const audio = new Audio(URL.createObjectURL(response.data));
        audio.addEventListener('ended', () => setPlayingAudio(null));
        audio.addEventListener('pause', () => setPlayingAudio(null));
        audioCache.current[record.id] = audio;
      }
      audioCache.current[record.id].play();
      setPlayingAudio(record.id);
    } catch { message.error('Unable to play audio'); }
  };

  // Read error message from blob response (when responseType: 'blob' but server returns JSON error)
  const extractBlobError = async (error) => {
    try {
      if (error?.response?.data instanceof Blob) {
        const text = await error.response.data.text();
        const json = JSON.parse(text);
        return json.message || text;
      }
    } catch { /* ignore */ }
    return error?.response?.data?.message || error?.message || 'Operation failed';
  };

  const handleDownloadSingle = async (record) => {
    try {
      const response = await apiClient.get(`/api/text-to-voice/audio/${record.id}`, { responseType: 'blob' });
      const contentType = response.headers['content-type'] || 'audio/mpeg';
      const ext = contentType.includes('wav') ? 'wav' : 'mp3';
      const safeName = record.text.substring(0, 30).replace(/[\\/:*?"<>|]/g, '_');
      const filename = `${record.stt}_${safeName}.${ext}`;
      const url = URL.createObjectURL(new Blob([response.data], { type: contentType }));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      const msg = await extractBlobError(error);
      message.error(msg);
    }
  };

  const downloadAsZip = async (ids, msgKey = 'batch-dl') => {
    message.loading({ content: `Packing ${ids.length} files...`, key: msgKey, duration: 0 });
    const response = await apiClient.post('/api/text-to-voice/batch-download',
      { request_ids: ids }, { responseType: 'blob' });
    const cd = response.headers['content-disposition'] || '';
    const match = /filename="?([^"]+)"?/i.exec(cd);
    const name = match ? match[1] : `audio_${Date.now()}.zip`;
    const url = URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
    const link = document.createElement('a'); link.href = url; link.download = name;
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    message.success({ content: `Downloaded ${ids.length} files`, key: msgKey });
  };

  const handleBatchDownload = async () => {
    if (selectedRowKeys.length === 0) { message.warning('Select at least 1 request'); return; }
    if (selectedRowKeys.length === 1) {
      const rec = requests.find(r => r.id === selectedRowKeys[0]);
      if (rec) handleDownloadSingle(rec);
      return;
    }
    try {
      setLoading(true);
      await downloadAsZip(selectedRowKeys);
    } catch (error) {
      const msg = await extractBlobError(error);
      message.error({ content: msg, key: 'batch-dl' });
    } finally { setLoading(false); }
  };

  const handleDownloadAll = async () => {
    const completedIds = requests.filter(r => r.status === 'completed').map(r => r.id);
    if (!completedIds.length) { message.warning('No completed files available'); return; }
    try {
      setLoading(true);
      await downloadAsZip(completedIds, 'dl-all');
    } catch (error) {
      const msg = await extractBlobError(error);
      message.error({ content: msg, key: 'dl-all' });
    } finally { setLoading(false); }
  };

  const handleVoiceDemo = async (voice) => {
    try {
      setLoading(true);
      if (playingDemo && audioCache.current[playingDemo]) {
        audioCache.current[playingDemo].pause();
        audioCache.current[playingDemo].currentTime = 0;
      }
      const response = await apiClient.get(`/api/voices/file/${voice.code}`, { responseType: 'blob' });
      const audio = new Audio(URL.createObjectURL(
        new Blob([response.data], { type: response.headers['content-type'] || 'audio/mpeg' })
      ));
      audio.addEventListener('ended', () => setPlayingDemo(null));
      audioCache.current[voice.code] = audio;
      setPlayingDemo(voice.code);
      await audio.play();
    } catch (error) {
      const status = error?.response?.status;
      message.error(status === 404 ? `Demo not found for ${voice.name}` : `Demo playback error: ${error.message}`);
    } finally { setLoading(false); }
  };

  const handleBatchSynthesis = async () => {
    if (selectedRowKeys.length === 0) { message.warning('Select at least 1 request'); return; }
    try {
      const pending = requests.filter(r => selectedRowKeys.includes(r.id) && r.status === 'pending');
      if (pending.length === 0) { message.warning('No pending requests to synthesize'); return; }
      await apiClient.post('/api/text-to-voice/batch-synthesize', { request_ids: pending.map(r => r.id) });
      message.success(`Synthesizing ${pending.length} requests...`);
      setSelectedRowKeys([]);
      fetchRequests(true);
    } catch { message.error('Batch synthesis failed'); }
  };

  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) { message.warning('Select at least 1 request'); return; }
    try {
      await apiClient.post('/api/text-to-voice/batch-delete', { request_ids: selectedRowKeys });
      message.success(`Deleted ${selectedRowKeys.length} requests`);
      setSelectedRowKeys([]);
      fetchRequests(true);
    } catch { message.error('Batch delete failed'); }
  };

  const handleRetryFailedAll = async () => {
    try {
      const failed = requests.filter(r => r.status === 'failed');
      if (failed.length === 0) { message.info('No failed requests'); return; }
      setLoading(true);
      await apiClient.post('/api/text-to-voice/batch-process', { request_ids: failed.map(r => r.id) });
      message.success(`Retrying ${failed.length} failed requests...`);
      fetchRequests(true);
    } catch { message.error('Retry failed'); }
    finally { setLoading(false); }
  };

  const handleMergeAudio = () => {
    if (requests.filter(r => r.status === 'completed').length < 2) {
      message.warning('At least 2 completed audio files are needed to merge');
      return;
    }
    setMergeModalVisible(true);
  };

  const handleMergeSubmit = async (values) => {
    let pollTimer = null;
    try {
      setMergingAudio(true);
      const completed = requests.filter(r => r.status === 'completed').sort((a, b) => a.stt - b.stt);
      let filename = values.output_filename || 'merged_audio.mp3';
      if (!/\.(wav|mp3)$/i.test(filename)) filename = `${filename.replace(/\.(mp3|m4a|aac|ogg|wav)$/i, '')}.mp3`;

      // Step 1: send request → receive job_id immediately, no waiting for processing
      message.loading({ content: `Initializing merge of ${completed.length} files...`, key: 'merge', duration: 0 });
      const { data: { job_id } } = await apiClient.post('/api/text-to-voice/merge', {
        request_ids: completed.map(r => r.id),
        add_silence: Number(values.add_silence ?? 0.5),
        output_filename: filename,
      });

      // Step 2: poll every 2s until done/failed (timeout 15 minutes, max 5 consecutive errors)
      await new Promise((resolve, reject) => {
        const POLL_TIMEOUT_MS = 15 * 60 * 1000;
        const MAX_POLL_ERRORS = 5;
        const pollDeadline = Date.now() + POLL_TIMEOUT_MS;
        let consecutiveErrors = 0;

        pollTimer = setInterval(async () => {
          if (Date.now() > pollDeadline) {
            clearInterval(pollTimer);
            reject(new Error('Merge audio exceeded the allowed time (15 minutes), please try again'));
            return;
          }
          try {
            const { data } = await apiClient.get(`/api/text-to-voice/merge/status/${job_id}`);
            consecutiveErrors = 0;
            const { status, progress, error: jobError } = data;
            if (status === 'pending' || status === 'processing') {
              if (progress?.step === 'trimming') {
                message.loading({ content: `Processing ${progress.current}/${progress.total} files...`, key: 'merge', duration: 0 });
              } else if (progress?.step === 'merging') {
                message.loading({ content: 'Merging final file...', key: 'merge', duration: 0 });
              }
            } else if (status === 'done') {
              clearInterval(pollTimer);
              resolve();
            } else if (status === 'failed') {
              clearInterval(pollTimer);
              reject(new Error(jobError || 'Merge audio failed'));
            }
          } catch (err) {
            consecutiveErrors += 1;
            if (consecutiveErrors >= MAX_POLL_ERRORS) {
              clearInterval(pollTimer);
              reject(new Error('Lost connection to server, please try again'));
            }
          }
        }, 2000);
      });

      // Step 3: download the file
      message.loading({ content: 'Downloading...', key: 'merge', duration: 0 });
      const response = await apiClient.get(`/api/text-to-voice/merge/download/${job_id}`, { responseType: 'blob' });
      const cd = response.headers['content-disposition'] || '';
      const match = /filename="?([^"]+)"?/i.exec(cd);
      const dlName = match ? match[1] : filename;
      const url = URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] || 'audio/mpeg' }));
      const link = document.createElement('a'); link.href = url; link.download = dlName;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      message.success({ content: `Downloaded: ${dlName}`, key: 'merge' });
      setMergeModalVisible(false);
    } catch (error) {
      if (pollTimer) clearInterval(pollTimer);
      const msg = error.message || 'Merge audio failed';
      message.error({ content: msg, key: 'merge' });
    } finally {
      setMergingAudio(false);
    }
  };

  const handleExcelUpload = (file) => { setExcelFile(file); setImportType('excel'); setExcelVoiceModalVisible(true); return false; };
  const handleTxtUpload = (file) => { setTxtFile(file); setImportType('txt'); setExcelVoiceModalVisible(true); return false; };

  const handleExcelImport = async () => {
    if (!selectedVoice) { message.warning('Select a voice'); return; }
    if (!excelFile && !txtFile) { message.warning('No file selected'); return; }
    try {
      let reqs = [], totalChars = 0;
      if (importType === 'excel' && excelFile) {
        const data = await excelFile.arrayBuffer();
        const wb = XLSX.read(data);
        const ws = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(ws, { header: 1 });
        for (let i = 0; i < json.length; i++) {
          const row = json[i];
          if (row[0] && row[1]) {
            const text = String(row[1]);
            reqs.push({ stt: parseInt(row[0]) || i + 1, text, voice_code: selectedVoice, ...(selectedProject && { project: selectedProject }) });
            totalChars += text.length;
          }
        }
      } else if (importType === 'txt' && txtFile) {
        const text = await txtFile.text();
        const lines = text.split('\n').filter(l => l.trim());
        lines.forEach((line, i) => {
          reqs.push({ stt: i + 1, text: line.trim(), voice_code: selectedVoice, ...(selectedProject && { project: selectedProject }) });
          totalChars += line.length;
        });
      }
      if (reqs.length === 0) { message.warning('No valid data found'); return; }
      if (!user.is_admin && user.char_limit > 0) {
        const remaining = user.char_limit - user.used_chars;
        if (totalChars > remaining) {
          message.error(`Quota exceeded. ${remaining.toLocaleString()} characters remaining, ${totalChars.toLocaleString()} needed`);
          return;
        }
      }
      await apiClient.post('/api/text-to-voice/batch-import', reqs);
      const voiceName = voices.find(v => v.code === selectedVoice)?.name || selectedVoice;
      message.success(`Imported ${reqs.length} requests with voice: ${voiceName}`);
      fetchRequests(true);
      setExcelVoiceModalVisible(false);
      setSelectedVoice(''); setExcelFile(null); setTxtFile(null);
    } catch (error) {
      message.error(error.response?.data?.detail || 'Import failed');
    }
  };

  // ─── helpers ────────────────────────────────────────────────────────────────
  const getStatusColor = (s) => ({ completed: 'success', processing: 'processing', failed: 'error' }[s] || 'default');
  const getStatusLabel = (s) => ({ completed: 'Completed', processing: 'Processing', failed: 'Failed', pending: 'Pending' }[s] || s);

  const stats = {
    total: requests.length,
    completed: requests.filter(r => r.status === 'completed').length,
    processing: requests.filter(r => r.status === 'processing').length,
    pending: requests.filter(r => r.status === 'pending').length,
    failed: requests.filter(r => r.status === 'failed').length,
    totalChars: requests.reduce((s, r) => s + (r.chars || 0), 0),
    completedChars: requests.filter(r => r.status === 'completed').reduce((s, r) => s + (r.chars || 0), 0),
  };
  const completionRate = stats.totalChars > 0 ? Math.round((stats.completedChars / stats.totalChars) * 100) : 0;

  const allSelected = requests.length > 0 && requests.every(r => selectedRowKeys.includes(r.id));
  const someSelected = selectedRowKeys.length > 0 && !allSelected;

  // ─── voice selector option ────────────────────────────────────────────────
  const renderVoiceOption = (voice) => (
    <Option key={voice.code} value={voice.code}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>
          <strong>{voice.name}</strong>
          <Tag color={voice.gender === 'female' ? 'pink' : 'blue'} style={{ marginLeft: 6, fontSize: 10 }}>
            {voice.gender}
          </Tag>
          {voice.language && <Text type="secondary" style={{ fontSize: 11, marginLeft: 4 }}>{voice.language}</Text>}
        </span>
        <Button
          size="small" type="link" icon={<PlayCircleOutlined />}
          onClick={e => { e.stopPropagation(); handleVoiceDemo(voice); }}
          style={{ padding: '0 4px', fontSize: 11 }}
        >
          Demo
        </Button>
      </div>
    </Option>
  );

  // ─── table columns ────────────────────────────────────────────────────────
  const columns = [
    { title: 'STT', dataIndex: 'stt', key: 'stt', width: 56, align: 'center' },
    {
      title: 'Content', dataIndex: 'text', key: 'text',
      render: (text) => (
        <Tooltip title={text}>
          <span style={{ cursor: 'default' }}>
            {text?.length > 80 ? text.substring(0, 80) + '…' : text}
          </span>
        </Tooltip>
      ),
    },
    { title: 'Chars', dataIndex: 'chars', key: 'chars', width: 70, align: 'center' },
    {
      title: 'Project', dataIndex: 'project', key: 'project', width: 130,
      render: p => p ? <Tag>{p}</Tag> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 120, align: 'center',
      render: s => <Badge status={getStatusColor(s)} text={getStatusLabel(s)} />,
    },
    {
      title: 'Voice', dataIndex: 'voice_code', key: 'voice_code', width: 160,
      render: code => voices.find(v => v.code === code)?.name || code,
    },
    {
      title: 'Audio', key: 'audio', width: 90, align: 'center',
      render: (_, record) => {
        if (!record.audio_file_path || record.status !== 'completed') return null;
        const playing = playingAudio === record.id;
        return (
          <Space size={4}>
            <Tooltip title={playing ? 'Pause' : 'Play'}>
              <Button
                type="text" size="small"
                icon={playing ? <PauseCircleOutlined style={{ color: '#1890ff', fontSize: 18 }} />
                             : <PlayCircleOutlined style={{ color: '#1890ff', fontSize: 18 }} />}
                onClick={() => handlePlayAudio(record)}
              />
            </Tooltip>
            <Tooltip title="Download">
              <Button
                type="text" size="small"
                icon={<DownloadOutlined style={{ fontSize: 16 }} />}
                onClick={() => handleDownloadSingle(record)}
              />
            </Tooltip>
          </Space>
        );
      },
    },
    {
      title: 'Actions', key: 'actions', width: 130, align: 'center',
      render: (_, record) => (
        <Space size={2}>
          <Tooltip title={record.status === 'completed' ? 'Already completed' : 'Edit'}>
            <Button
              type="text" size="small" icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              disabled={record.status === 'completed'}
            />
          </Tooltip>

          {record.status === 'pending' && (
            <Tooltip title="Synthesize now">
              <Button
                type="text" size="small" icon={<SoundOutlined style={{ color: '#52c41a' }} />}
                onClick={() => handleSynthesize(record.id)}
              />
            </Tooltip>
          )}

          {record.status === 'failed' && (
            <Tooltip title="Retry">
              <Button
                type="text" size="small" icon={<RedoOutlined style={{ color: '#fa8c16' }} />}
                onClick={() => handleRetry(record.id)}
              />
            </Tooltip>
          )}

          {record.status === 'completed' && !record.audio_file_path && (
            <Tooltip title="Re-download audio file">
              <Button
                type="text" size="small" icon={<DownloadOutlined style={{ color: '#fa8c16' }} />}
                onClick={() => handleRetryDownload(record.id)}
              />
            </Tooltip>
          )}

          <Tooltip title="Delete">
            <Popconfirm
              title="Delete this request?"
              onConfirm={() => handleDelete(record.id)}
              okText="Delete" cancelText="Cancel"
            >
              <Button type="text" size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  // ─── render ────────────────────────────────────────────────────────────────
  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const failedCount = requests.filter(r => r.status === 'failed').length;
  const completedCount = requests.filter(r => r.status === 'completed').length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="page-header" style={{ marginBottom: 0 }}>
        <h1 style={{ margin: 0 }}>Text to Voice</h1>
        <p style={{ margin: 0 }}>Synthesize voice from text</p>
      </div>

      {/* ── Quota warning ───────────────────────────────────────────────────── */}
      {!user.is_admin && user.char_limit === 0 && (
        <Alert
          message="No character quota assigned"
          description="Contact admin to receive a voice synthesis quota."
          type="warning" showIcon
        />
      )}

      {/* ── Stats bar ───────────────────────────────────────────────────────── */}
      <Card size="small" bodyStyle={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px 24px', alignItems: 'center' }}>
          <span style={{ color: '#333', fontWeight: 600 }}>{stats.total} requests</span>
          <Divider type="vertical" />
          {stats.pending > 0   && <Badge status="default"     text={<span>Pending: <b>{stats.pending}</b></span>} />}
          {stats.processing > 0 && <Badge status="processing"  text={<span>Processing: <b>{stats.processing}</b></span>} />}
          {stats.completed > 0  && <Badge status="success"     text={<span>Completed: <b>{stats.completed}</b></span>} />}
          {stats.failed > 0     && <Badge status="error"       text={<span>Failed: <b>{stats.failed}</b></span>} />}
          {stats.totalChars > 0 && (
            <>
              <Divider type="vertical" />
              <span style={{ color: '#888', fontSize: 13 }}>
                {stats.completedChars.toLocaleString()}/{stats.totalChars.toLocaleString()} chars
              </span>
              <Progress
                percent={completionRate} size="small" showInfo={false}
                strokeColor="#1890ff"
                style={{ width: 100, margin: 0 }}
              />
              <span style={{ color: '#888', fontSize: 12 }}>{completionRate}%</span>
            </>
          )}
          {!user.is_admin && user.char_limit > 0 && (
            <>
              <Divider type="vertical" />
              <span style={{ fontSize: 12, color: '#888' }}>
                Quota: <b>{(user.char_limit - user.used_chars).toLocaleString()}</b> characters remaining
              </span>
            </>
          )}
        </div>
      </Card>

      {/* ── Main Card ───────────────────────────────────────────────────────── */}
      <Card>
        {/* Toolbar */}
        <div className="ttv-toolbar">

          {/* Row 1: Project filter + Select all + Refresh */}
          <div className="ttv-toolbar-top">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <Text type="secondary" style={{ fontSize: 13, whiteSpace: 'nowrap' }}>Project:</Text>
              <AutoComplete
                placeholder="All — type a name to create / filter project"
                value={selectedProject}
                onChange={v => setSelectedProject(v || '')}
                allowClear
                style={{ minWidth: 220 }}
                options={[
                  ...(availableProjects.length > 0
                    ? availableProjects
                        .filter(p => !selectedProject || p.toLowerCase().includes(selectedProject.toLowerCase()))
                        .map(p => ({ value: p, label: <><Tag style={{ marginRight: 4 }}>{p}</Tag></> }))
                    : []),
                  ...(selectedProject && !availableProjects.includes(selectedProject)
                    ? [{ value: selectedProject, label: <Text type="success">+ Create project "{selectedProject}"</Text> }]
                    : []),
                ]}
              />
              {selectedProject && (
                <Tag color="blue" closable onClose={() => setSelectedProject('')}>
                  {selectedProject}
                </Tag>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={e => setSelectedRowKeys(e.target.checked ? requests.map(r => r.id) : [])}
              >
                Select all
              </Checkbox>
              {selectedRowKeys.length > 0 && (
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Selected: <b>{selectedRowKeys.length}</b>
                </Text>
              )}
              <Tooltip title="Refresh">
                <Button icon={<ReloadOutlined />} onClick={() => fetchRequests(true)} />
              </Tooltip>
            </div>
          </div>

          <Divider style={{ margin: '10px 0' }} />

          {/* Row 2: Action groups */}
          <div className="ttv-toolbar-actions">

            {/* Group A: Create new */}
            <div className="ttv-action-group">
              <Text type="secondary" style={{ fontSize: 11, marginBottom: 4, display: 'block' }}>CREATE</Text>
              <Space wrap size={6}>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                  Add
                </Button>
                <Button icon={<ScissorOutlined />} onClick={() => setScriptSplitterVisible(true)}>
                  Script Splitter
                </Button>
                <Upload accept=".xlsx,.xls" beforeUpload={handleExcelUpload} showUploadList={false}>
                  <Button icon={<UploadOutlined />}>Excel</Button>
                </Upload>
                <Upload accept=".txt" beforeUpload={handleTxtUpload} showUploadList={false}>
                  <Button icon={<UploadOutlined />}>TXT</Button>
                </Upload>
                <Tooltip title="View pricing / upgrade plan">
                  <Button icon={<DollarOutlined />} onClick={() => navigate('/pricing')}>Pricing</Button>
                </Tooltip>
              </Space>
            </div>

            <Divider type="vertical" style={{ height: 'auto', alignSelf: 'stretch', margin: '0 8px' }} />

            {/* Group B: Process */}
            <div className="ttv-action-group">
              <Text type="secondary" style={{ fontSize: 11, marginBottom: 4, display: 'block' }}>PROCESS</Text>
              <Space wrap size={6}>
                <Tooltip title={pendingCount === 0 ? 'No pending requests' : ''}>
                  <Button
                    type="primary" ghost icon={<PlayCircleOutlined />}
                    onClick={handleBatchSynthesis}
                    disabled={selectedRowKeys.length === 0}
                  >
                    Synthesize{selectedRowKeys.length > 0 ? ` (${selectedRowKeys.length})` : ''}
                  </Button>
                </Tooltip>
                <Tooltip title={failedCount === 0 ? 'No failed requests' : `Retry ${failedCount} failed requests`}>
                  <Button
                    icon={<RedoOutlined />} onClick={handleRetryFailedAll}
                    disabled={failedCount === 0 || loading}
                    danger={failedCount > 0}
                  >
                    Retry failed{failedCount > 0 ? ` (${failedCount})` : ''}
                  </Button>
                </Tooltip>
              </Space>
            </div>

            <Divider type="vertical" style={{ height: 'auto', alignSelf: 'stretch', margin: '0 8px' }} />

            {/* Group C: Download */}
            <div className="ttv-action-group">
              <Text type="secondary" style={{ fontSize: 11, marginBottom: 4, display: 'block' }}>DOWNLOAD</Text>
              <Space wrap size={6}>
                <Tooltip title={selectedRowKeys.length === 0 ? 'Select requests to download' : `Download ${selectedRowKeys.length} selected files`}>
                  <Button
                    icon={<DownloadOutlined />} onClick={handleBatchDownload}
                    disabled={selectedRowKeys.length === 0}
                  >
                    Download{selectedRowKeys.length > 0 ? ` (${selectedRowKeys.length})` : ''}
                  </Button>
                </Tooltip>
                <Tooltip title={completedCount === 0 ? 'No completed files' : `Download all ${completedCount} completed files as ZIP`}>
                  <Button
                    icon={<DownloadOutlined />} onClick={handleDownloadAll}
                    disabled={completedCount === 0 || loading}
                  >
                    Download all{completedCount > 0 ? ` (${completedCount})` : ''}
                  </Button>
                </Tooltip>
                <Tooltip title={completedCount < 2 ? 'At least 2 completed files required' : 'Merge all audio into 1 file'}>
                  <Button
                    icon={<MergeOutlined />} onClick={handleMergeAudio}
                    loading={mergingAudio}
                    disabled={completedCount < 2 || mergingAudio}
                  >
                    Merge audio{completedCount >= 2 ? ` (${completedCount})` : ''}
                  </Button>
                </Tooltip>
              </Space>
            </div>

            {/* Group D: Delete (separated, placed last) */}
            {selectedRowKeys.length > 0 && (
              <>
                <Divider type="vertical" style={{ height: 'auto', alignSelf: 'stretch', margin: '0 8px' }} />
                <div className="ttv-action-group">
                  <Text type="secondary" style={{ fontSize: 11, marginBottom: 4, display: 'block' }}>DANGER</Text>
                  <Popconfirm
                    title={`Delete ${selectedRowKeys.length} selected requests?`}
                    onConfirm={handleBatchDelete}
                    okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
                  >
                    <Button danger icon={<DeleteOutlined />}>
                      Delete ({selectedRowKeys.length})
                    </Button>
                  </Popconfirm>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="table-responsive" style={{ marginTop: 16 }}>
          <Table
            columns={columns}
            dataSource={requests || []}
            rowKey="id"
            loading={loading}
            size="small"
            rowSelection={{
              selectedRowKeys,
              onChange: keys => setSelectedRowKeys(keys),
            }}
            scroll={{ x: 900 }}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}`,
              pageSizeOptions: ['10', '20', '50', '100', '200', '500'],
              onChange: (page, pageSize) => {
                setPagination(prev => ({ ...prev, current: page, pageSize }));
                fetchRequests(true, page, pageSize);
              },
              onShowSizeChange: (_, size) => {
                setPagination(prev => ({ ...prev, current: 1, pageSize: size }));
                fetchRequests(true, 1, size);
              },
            }}
          />
        </div>
      </Card>

      {/* ── Modal: Add / Edit request ──────────────────────────────────────── */}
      <Modal
        title={editingRequest ? 'Edit request' : 'Add new request'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={560}
        className="text-to-voice-modal"
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="stt" label="STT" rules={[
            { required: true, message: 'Enter STT' },
            { validator: (_, v) => !isNaN(Number(v)) ? Promise.resolve() : Promise.reject('STT must be a number') },
          ]}>
            <Input type="number" />
          </Form.Item>

          <Form.Item name="project" label="Project (optional)">
            <AutoComplete
              placeholder="Select existing project or type a new name"
              allowClear
              options={availableProjects.map(p => ({ value: p }))}
              filterOption={(input, option) =>
                (option?.value || '').toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item name="text" label="Content" rules={[
            { required: true, message: 'Enter content' },
            { validator: (_, v) => v?.length > 1000 ? Promise.reject('Maximum 1000 characters') : Promise.resolve() },
          ]}>
            <TextArea rows={4} maxLength={1000} showCount placeholder="Enter content (max 1000 characters)" />
          </Form.Item>

          <Form.Item name="voice_code" label="Voice" rules={[{ required: true, message: 'Select a voice' }]}>
            <Select placeholder="Select voice" showSearch
              filterOption={(input, option) =>
                option.children?.props?.children?.[0]?.props?.children?.[0]?.props?.children
                  ?.toLowerCase().includes(input.toLowerCase()) ?? false
              }
            >
              {safeMap(voices, renderVoiceOption)}
            </Select>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">{editingRequest ? 'Update' : 'Create'}</Button>
              <Button onClick={() => setModalVisible(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Modal: Import voice selection ─────────────────────────────────── */}
      <Modal
        title={`Select voice for ${importType.toUpperCase()} import`}
        open={excelVoiceModalVisible}
        onCancel={() => { setExcelVoiceModalVisible(false); setSelectedVoice(''); setExcelFile(null); setTxtFile(null); }}
        onOk={handleExcelImport}
        okText="Import" cancelText="Cancel"
        width={480}
      >
        {(excelFile || txtFile) && (
          <Alert
            type="info" showIcon style={{ marginBottom: 12 }}
            message={
              <span>
                File: <b>{(excelFile || txtFile)?.name}</b> —{' '}
                {((excelFile || txtFile)?.size / 1024).toFixed(1)} KB
                {selectedProject && <span> · Project: <Tag color="blue">{selectedProject}</Tag></span>}
              </span>
            }
          />
        )}
        {!selectedProject && (
          <Alert
            type="warning" showIcon style={{ marginBottom: 12 }}
            message="No project selected. Requests will not be assigned to a project. You can select a project from the toolbar before importing."
          />
        )}
        <Form.Item label="Voice" required>
          <Select
            placeholder="Select voice" value={selectedVoice} onChange={setSelectedVoice}
            style={{ width: '100%' }} showSearch
            filterOption={(input, option) =>
              (option?.children?.props?.children?.[0]?.props?.children?.[0]?.props?.children || '')
                .toLowerCase().includes(input.toLowerCase())
            }
          >
            {voices.map(renderVoiceOption)}
          </Select>
        </Form.Item>
      </Modal>

      {/* ── Modal: Merge audio ────────────────────────────────────────────── */}
      <Modal
        title="Merge audio"
        open={mergeModalVisible}
        onCancel={() => setMergeModalVisible(false)}
        footer={null} width={520}
      >
        <Alert
          type="info" showIcon style={{ marginBottom: 16 }}
          message={`Will merge ${completedCount} audio files in STT order`}
        />
        <Form layout="vertical" onFinish={handleMergeSubmit}>
          <Form.Item name="output_filename" label="Output filename" initialValue="merged_audio"
            rules={[{ required: true, message: 'Enter file name' }]}>
            <Input addonAfter=".mp3" placeholder="merged_audio" />
          </Form.Item>
          <Form.Item name="add_silence" label="Silence between segments (seconds)" initialValue={0.5}>
            <Input type="number" step="0.1" min="0" max="5" addonAfter="sec" />
          </Form.Item>
          <div style={{ maxHeight: 160, overflowY: 'auto', background: '#fafafa', borderRadius: 6, padding: '8px 12px', marginBottom: 16, fontSize: 12, color: '#666' }}>
            {requests.filter(r => r.status === 'completed').sort((a, b) => a.stt - b.stt).map((r, i) => (
              <div key={r.id}>STT {r.stt}: {r.text.substring(0, 60)}{r.text.length > 60 ? '…' : ''}</div>
            ))}
          </div>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={mergingAudio} icon={<AudioOutlined />}>
                {mergingAudio ? 'Merging...' : 'Start merging'}
              </Button>
              <Button onClick={() => setMergeModalVisible(false)}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Script Splitter ──────────────────────────────────────────────── */}
      <ScriptSplitter
        visible={scriptSplitterVisible}
        onClose={() => setScriptSplitterVisible(false)}
        onSplitAndCreate={fetchRequests}
      />
    </div>
  );
}

export default TextToVoice;
